import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { wallApi } from '../api/api';
import MatrixBackground from '../components/wall/MatrixBackground';
import DOMPurify from 'dompurify';
import PostBubble from '../components/wall/PostBubble';
import { Button } from '../components/ui/button';
import { Users, Heart, FileText, Loader2, ArrowLeft, UserPlus, UserMinus, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/layout/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getWallAvatarUrl = (wall) => {
  if (!wall?.profile_image) return null;
  const img = wall.profile_image;
  if (img.startsWith('http') || (img.startsWith('/') && !img.startsWith('/api/'))) return img;
  if (wall.slug) return `${BACKEND_URL}/api/walls/avatar/${wall.slug}`;
  return `${BACKEND_URL}${img}`;
};
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

export default function WallViewPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [wall, setWall] = useState(null);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);
  const [followType, setFollowType] = useState(null); // 'follower' or 'subscriber'

  useEffect(() => {
    if (!authLoading && isAuthenticated && slug) {
      loadWall();
    }
  }, [authLoading, isAuthenticated, slug]);

  const loadWall = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get wall info
      const wallData = await wallApi.getWallBySlug(slug);
      setWall(wallData);

      // Get posts if subscribed or owner
      if (wallData.is_subscribed || wallData.is_owner) {
        const postsData = await wallApi.getWallPosts(slug);
        setPosts(postsData.posts);
      }
    } catch (err) {
      console.error('Error loading wall:', err);
      if (err.response?.status === 404) {
        setError('Wall not found');
      } else if (err.response?.status === 403) {
        setError('Subscribe to view posts');
      } else {
        setError('Failed to load wall');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    try {
      const result = await wallApi.follow(slug);
      setWall({ ...wall, is_subscribed: true, subscriber_count: wall.subscriber_count + 1 });
      setFollowType('follower');
      toast.success('Now following! You can view posts.');
      // Load posts after following
      const postsData = await wallApi.getWallPosts(slug);
      setPosts(postsData.posts);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to follow');
    }
  };

  const handleSubscribe = async () => {
    try {
      const result = await wallApi.subscribe(slug);
      setWall({ ...wall, is_subscribed: true, subscriber_count: wall.subscriber_count + (followType ? 0 : 1) });
      setFollowType('subscriber');
      toast.success('Subscribed! You\'ll get notifications for new posts.');
      // Load posts if not already loaded
      if (posts.length === 0) {
        const postsData = await wallApi.getWallPosts(slug);
        setPosts(postsData.posts);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to subscribe');
    }
  };

  const handleUnsubscribe = async () => {
    try {
      await wallApi.unsubscribe(slug);
      setWall({ ...wall, is_subscribed: false, subscriber_count: wall.subscriber_count - 1 });
      setFollowType(null);
      setPosts([]);
      toast.success('Unfollowed');
    } catch (err) {
      toast.error('Failed to unfollow');
    }
  };

  const handleToggleNotifications = async () => {
    try {
      const newType = followType === 'subscriber' ? 'follower' : 'subscriber';
      await wallApi.updateFollowType(slug, newType);
      setFollowType(newType);
      toast.success(newType === 'subscriber' ? 'Notifications enabled!' : 'Notifications disabled');
    } catch (err) {
      toast.error('Failed to update notification settings');
    }
  };

  const handleLike = async (postId) => {
    try {
      const result = await wallApi.toggleLike(postId);
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, is_liked: result.liked, like_count: result.like_count } : p
      ));
    } catch (err) {
      toast.error('Failed to update like');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0a0a18] flex items-center justify-center">
        <MatrixBackground opacity={0.1} />
        <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
      </div>
    );
  }

  if (error === 'Wall not found') {
    return (
      <div className="min-h-screen bg-[#0a0a18]">
        <MatrixBackground opacity={0.1} />
        <Header />
        <div className="relative z-10 max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Wall Not Found</h1>
          <p className="text-gray-400 mb-6">This wall doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/wall')} variant="outline" className="border-[#6D4AFF]/50 text-[#6D4AFF]">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Wall
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a18]">
      <MatrixBackground opacity={0.15} />
      <Header />
      
      <div className="relative z-10 max-w-4xl mx-auto px-4 py-6">
        {/* Back button */}
        <Button 
          onClick={() => navigate('/wall')} 
          variant="ghost" 
          className="text-gray-400 hover:text-white mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Wall Header */}
        <div className="bg-gradient-to-br from-[#1a1a2e]/90 to-[#16162a]/90 backdrop-blur-xl rounded-2xl p-6 border border-[#6D4AFF]/20 mb-6 shadow-[0_0_30px_rgba(109,74,255,0.1)]">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {/* Profile */}
            <div className="flex items-center gap-4">
              {getWallAvatarUrl(wall) ? (
                <img 
                  src={getWallAvatarUrl(wall)} 
                  alt={wall.name} 
                  className="w-20 h-20 rounded-full object-cover border-2 border-[#6D4AFF] shadow-[0_0_20px_rgba(109,74,255,0.3)]"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#00bcd4] flex items-center justify-center text-white text-3xl font-bold shadow-[0_0_20px_rgba(109,74,255,0.3)]">
                  {wall?.name?.charAt(0) || 'W'}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-white">{wall?.name}</h1>
                <p className="text-gray-400">@{wall?.slug}</p>
              </div>
            </div>

            {/* Stats & Actions */}
            <div className="flex-1 flex flex-col md:flex-row md:items-center md:justify-end gap-4">
              <div className="flex items-center gap-6 text-gray-400">
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{wall?.subscriber_count || 0}</div>
                  <div className="text-xs">Subscribers</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{wall?.post_count || 0}</div>
                  <div className="text-xs">Posts</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-white">{wall?.total_likes || 0}</div>
                  <div className="text-xs">Likes</div>
                </div>
              </div>

              {!wall?.is_owner && (
                wall?.is_subscribed ? (
                  <div className="flex items-center gap-2">
                    {/* Notification toggle */}
                    <Button 
                      onClick={handleToggleNotifications}
                      variant="outline"
                      size="icon"
                      className={followType === 'subscriber' 
                        ? "border-[#6D4AFF]/50 text-[#6D4AFF] hover:bg-[#6D4AFF]/10" 
                        : "border-gray-500/50 text-gray-400 hover:bg-gray-500/10"
                      }
                      title={followType === 'subscriber' ? 'Notifications ON' : 'Notifications OFF'}
                    >
                      {followType === 'subscriber' ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                    </Button>
                    
                    {/* Unfollow dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="outline"
                          className="border-gray-500/50 text-gray-300 hover:bg-gray-500/10"
                        >
                          {followType === 'subscriber' ? (
                            <>
                              <Bell className="w-4 h-4 mr-2 text-[#6D4AFF]" />
                              Subscribed
                            </>
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-2" />
                              Following
                            </>
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#1a1a2e] border-[#2d2d44]">
                        <DropdownMenuItem 
                          onClick={handleToggleNotifications}
                          className="text-gray-300 hover:text-white focus:bg-[#252540]"
                        >
                          {followType === 'subscriber' ? (
                            <>
                              <BellOff className="w-4 h-4 mr-2" />
                              Turn off notifications
                            </>
                          ) : (
                            <>
                              <Bell className="w-4 h-4 mr-2" />
                              Turn on notifications
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={handleUnsubscribe}
                          className="text-red-400 hover:text-red-300 focus:bg-[#252540]"
                        >
                          <UserMinus className="w-4 h-4 mr-2" />
                          Unfollow
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button className="bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]">
                        <UserPlus className="w-4 h-4 mr-2" />
                        Follow
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#1a1a2e] border-[#2d2d44]">
                      <DropdownMenuItem 
                        onClick={handleFollow}
                        className="text-gray-300 hover:text-white focus:bg-[#252540]"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Follow (no notifications)
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={handleSubscribe}
                        className="text-[#6D4AFF] hover:text-[#8B5CF6] focus:bg-[#252540]"
                      >
                        <Bell className="w-4 h-4 mr-2" />
                        Subscribe (with notifications)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )
              )}
            </div>
          </div>

          {/* Description */}
          {wall?.description && (
            <div 
              className="mt-4 pt-4 border-t border-[#6D4AFF]/20 text-gray-300"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(wall.description, { ALLOWED_TAGS: ['b','i','em','strong','a','br','p','ul','ol','li','h1','h2','h3','img','blockquote','code','span'], ALLOWED_ATTR: ['href','src','alt','class','target','rel'] }) }}
            />
          )}
        </div>

        {/* Posts */}
        {wall?.is_subscribed || wall?.is_owner ? (
          posts.length > 0 ? (
            <div className="max-w-2xl mx-auto space-y-4">
              {posts.map(post => (
                <PostBubble 
                  key={post.id} 
                  post={post} 
                  onLike={handleLike}
                  isOwner={wall?.is_owner}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No posts yet</p>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <div className="bg-[#1a1a2e]/80 backdrop-blur-xl rounded-2xl p-8 max-w-md mx-auto border border-[#6D4AFF]/20">
              <UserPlus className="w-12 h-12 mx-auto mb-4 text-[#6D4AFF]" />
              <h3 className="text-xl font-bold text-white mb-2">Subscribe to View</h3>
              <p className="text-gray-400 mb-4">Subscribe to this wall to see their posts</p>
              <Button 
                onClick={handleSubscribe}
                className="bg-gradient-to-r from-[#6D4AFF] to-[#8B5CF6]"
              >
                Subscribe Now
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
